import { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Divider,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectProjects,
  setSelectedProject,
  setSelectedMilestone,
  resetSelection,
  selectSelectedMilestone,
  selectInitializationStatus,
  selectSelectedProject,
  selectIsMilestoneCompletedOrFailed,
  selectIsMilestoneLoadingCompleted,
  selectIsMilestoneLoading,
} from "../features/projects/projectSlice";
import {
  fetchProjectsAsync,
  fetchMilestoneByIdAsync,
} from "../features/projects/projectApi";
import TaskReportTable from "../components/taskReportTable/TaskReportTable";
import ClockifyTimeEntryTable from "../components/clockifyTimeEntryTable/ClockifyTimeEntryTable";
import TypeOfWorkSummaryTable from "../components/typeOfWorkSummaryTable/TypeOfWorkSummaryTable";
import { Milestone } from "../features/projects/Milestone";
import ProjectProgressReport from "../components/projectProgressReport/ProjectProgressReport";
import {
  selectClockifyTimeEntries,
  selectClockifyLoading,
  selectClockifyPagination,
  selectClockifySortConfig,
  selectClockifySortApplied,
  setPage as setClockifyPage,
  setPageSize as setClockifyPageSize,
  setSortConfig as setClockifySortConfig,
  sortTimeEntries,
} from "../features/clockifyTimeEntries/clockifyTimeEntriesSlice";
import { fetchClockifyTimeEntriesAsync } from "../features/clockifyTimeEntries/clockifyTimeEntriesApi";
import {
  selectMilestoneTasks,
  selectTaskReportsLoading,
  selectTaskReportsPagination,
  selectTaskReportsSortConfig,
  selectTaskReportsSortApplied,
  setPage as setTaskReportsPage,
  setPageSize as setTaskReportsPageSize,
  setSortConfig as setTaskReportsSortConfig,
  sortTaskReports,
} from "../features/taskReports/taskReportsSlice";
import { fetchTaskReportsAsync } from "../features/taskReports/taskReportsApi";
import { loadMilestoneTaskEstimateChangedAlertsAsync } from "../features/alerts/milestoneTaskEstimateChangedAlertApi";
import { loadUserAlertReadAsync } from "../features/alerts/alertsApi";
import { AlertTypeContract } from "../features/alerts/AlertTypeContract";
import { loadClockifyTimeChangedAlertsByMilestoneIdAsync } from "../features/alerts/clockifyTimeChangedAlert/clockifyTimeEntryTimeChangedApi";
import { selectClockifyTimeEntryTimeAlertInitializationStatus } from "../features/alerts/alertsSlice";
import { selectMilestoneTaskEstimateAlertInitializationStatus } from "../features/alerts/alertsSlice";
import { selectPercentageCompleteChangedAlertInitializationStatus } from "../features/alerts/alertsSlice";
import { loadPercentageCompleteChangedAlertsByMilestoneIdAsync } from "../features/alerts/percentageCompleteChangedAlert/percentageCompleteChangedAlertApi";
import { AlertsTable } from "../components/alertsTable/AlertsTable";
import {
  selectNonReadSelectedMilestoneTaskEstimateAlerts,
  selectNonReadClockifyTimeChangedAlerts,
  selectNonReadPercentageCompleteChangedAlerts,
} from "../features/alerts/alertsSlice";
import RunImportButton from "../components/runImportButton/RunImportButton";
import SnapshotComparison from "../components/snapshotComparison/SnapshotComparison";
import MilestoneNotes from "../components/milestoneNotes/MilestoneNotes";
import DeveloperDashboard from "../components/developerDashboard/DeveloperDashboard";
import {
  selectAuthInitializationStatus,
  selectAuthenticatedRole,
} from "../features/authentication/authenticationSlice";

const defaultMilestone: Milestone = {
  id: 0,
  name: "",
  totalEstimate: [],
  actualBurnUp: [],
  estimateBurnUp: [],
  estimatedTotalHoursNeeded: [],
  totalTaskEstimates: [],
  totalOfEstimatedCompleted: [],
  totalTasks: [],
  state: "",
  gitLabCreateDate: "",
  isFetchingCompleted: false,
  openTaskCount: 0,
  totalTaskCount: 0,
  forecast: null,
  forecastHistory: [],
};

export const ProjectProgressPage = () => {
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const selectedMilestone = useAppSelector(selectSelectedMilestone);
  const selectedProject = useAppSelector(selectSelectedProject);
  const initializationStatus = useAppSelector(selectInitializationStatus);
  const authInitializationStatus = useAppSelector(
    selectAuthInitializationStatus
  );
  const userRole = useAppSelector(selectAuthenticatedRole);
  const isMilestoneLoading = useAppSelector(selectIsMilestoneLoading);
  const isMilestoneCompletedOrFailed = useAppSelector(
    selectIsMilestoneCompletedOrFailed
  );
  const isMilestoneFetchCompleted = useAppSelector(
    selectIsMilestoneLoadingCompleted
  );

  const milestoneTaskEstimateAlertInitializationStatus = useAppSelector(
    selectMilestoneTaskEstimateAlertInitializationStatus
  );
  const clockifyTimeEntryTimeAlertInitializationStatus = useAppSelector(
    selectClockifyTimeEntryTimeAlertInitializationStatus
  );
  const percentageCompleteChangedAlertInitializationStatus = useAppSelector(
    selectPercentageCompleteChangedAlertInitializationStatus
  );

  const timeEntries = useAppSelector(selectClockifyTimeEntries);
  const loading = useAppSelector(selectClockifyLoading);
  const pagination = useAppSelector(selectClockifyPagination);
  const sortConfig = useAppSelector(selectClockifySortConfig);
  const sortApplied = useAppSelector(selectClockifySortApplied);

  const taskReports = useAppSelector(selectMilestoneTasks);
  const taskReportsLoading = useAppSelector(selectTaskReportsLoading);
  const taskReportsPagination = useAppSelector(selectTaskReportsPagination);
  const taskReportsSortConfig = useAppSelector(selectTaskReportsSortConfig);
  const taskReportsSortApplied = useAppSelector(selectTaskReportsSortApplied);

  const milestoneTaskEstimateAlerts = useAppSelector(
    selectNonReadSelectedMilestoneTaskEstimateAlerts
  );
  const clockifyTimeChangedAlerts = useAppSelector(
    selectNonReadClockifyTimeChangedAlerts
  );
  const percentageCompleteChangedAlerts = useAppSelector(
    selectNonReadPercentageCompleteChangedAlerts
  );

  const { projectId, milestoneId } = useParams();
  const navigate = useNavigate();

  const [isAlertsExpanded, setIsAlertsExpanded] = useState(true);
  const [isTaskReportsExpanded, setIsTaskReportsExpanded] = useState(true);
  const [isClockifyTimeEntriesExpanded, setIsClockifyTimeEntriesExpanded] =
    useState(true);
  const [isTypeOfWorkSummaryExpanded, setIsTypeOfWorkSummaryExpanded] =
    useState(true);
  const [isDeveloperDashboardExpanded, setIsDeveloperDashboardExpanded] =
    useState(true);

  const loadProjects = useCallback(async () => {
    if (initializationStatus === "not-initialized") {
      dispatch(fetchProjectsAsync());
    }
  }, [dispatch, initializationStatus]);

  useEffect(() => {
    if (projects.length === 0 || projectId || milestoneId) {
      loadProjects();
    } else if (projects.length > 0 && !projectId && !milestoneId) {
      const fallbackProject =
        selectedProject && selectedProject.id > 0
          ? selectedProject
          : projects[0];
      const nonUnassigned = fallbackProject.milestones.filter((m) => m.name !== "Unassigned");
      const unassigned = fallbackProject.milestones.find((m) => m.name === "Unassigned");
      const fallbackMilestone =
        nonUnassigned.length > 0
          ? (selectedMilestone && selectedMilestone.id > 0 && selectedMilestone.name !== "Unassigned"
              ? nonUnassigned.find((m) => m.id === selectedMilestone.id) || nonUnassigned[0]
              : nonUnassigned[0])
          : unassigned || null;
      const milestone = fallbackMilestone || defaultMilestone;
      navigate(`/project/${fallbackProject.id}/milestone/${milestone.id}`, { replace: true });
    }
  }, [
    projectId,
    milestoneId,
    loadProjects,
    projects.length,
    projects,
    selectedProject,
    selectedMilestone,
    navigate,
  ]);

  const loadAlerts = useCallback(
    (milestoneId: number) => {
      if (
        initializationStatus !== "initialized" ||
        authInitializationStatus !== "initialized"
      ) {
        return;
      }
      dispatch(loadMilestoneTaskEstimateChangedAlertsAsync(milestoneId));
      dispatch(loadClockifyTimeChangedAlertsByMilestoneIdAsync(milestoneId));
      dispatch(
        loadPercentageCompleteChangedAlertsByMilestoneIdAsync(milestoneId)
      );
    },
    [dispatch, initializationStatus, authInitializationStatus]
  );

  const handleAlertRead = useCallback(
    (status: string, alertType: AlertTypeContract) => {
      if (status === "initialized" && initializationStatus === "initialized") {
        dispatch(loadUserAlertReadAsync({ alertType }));
      }
    },
    [dispatch, initializationStatus]
  );

  useEffect(() => {
    handleAlertRead(
      milestoneTaskEstimateAlertInitializationStatus,
      AlertTypeContract.MilestoneTaskEstimate
    );
  }, [handleAlertRead, milestoneTaskEstimateAlertInitializationStatus]);

  useEffect(() => {
    handleAlertRead(
      clockifyTimeEntryTimeAlertInitializationStatus,
      AlertTypeContract.TimeEntryTime
    );
  }, [handleAlertRead, clockifyTimeEntryTimeAlertInitializationStatus]);

  useEffect(() => {
    handleAlertRead(
      percentageCompleteChangedAlertInitializationStatus,
      AlertTypeContract.PercentageComplete
    );
  }, [handleAlertRead, percentageCompleteChangedAlertInitializationStatus]);

  useEffect(() => {
    if (initializationStatus === "not-initialized") {
      loadProjects();
    }
  }, [initializationStatus, loadProjects]);

  useEffect(() => {
    if (!(projects.length > 0 && projectId && milestoneId)) {
      return;
    }

    const currentProject =
      projects.find((p) => p.id === parseInt(projectId)) || projects[0];
    const milestones = currentProject.milestones || [];
    const currentMilestone =
      milestones.find((m) => m.id === parseInt(milestoneId)) || milestones[0];

    if (!currentMilestone) {
      dispatch(resetSelection());
      return;
    }

    dispatch(setSelectedProject(currentProject));
    dispatch(setSelectedMilestone(currentMilestone));

    if (currentMilestone.id > 0) {
      if (!currentMilestone.isFetchingCompleted && !isMilestoneLoading) {
        dispatch(fetchMilestoneByIdAsync(currentMilestone.id));
      } else {
        loadAlerts(currentMilestone.id);
      }
    }

    navigate(`/project/${currentProject.id}/milestone/${currentMilestone.id}`, { replace: true });
  }, [
    projects,
    projectId,
    milestoneId,
    dispatch,
    navigate,
    loadAlerts,
    isMilestoneCompletedOrFailed,
    isMilestoneLoading,
  ]);

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    const project = projects.find((p) => p.name === event.target.value);
    if (typeof project !== "undefined") {
      dispatch(setSelectedProject(project));

      const nonUnassigned = project.milestones.filter((m) => m.name !== "Unassigned");
      const milestone = nonUnassigned.find(
        (m) => m.name === selectedMilestone.name
      );

      const selectedMilestoneToSet =
        milestone || nonUnassigned[0] || defaultMilestone;

      dispatch(setSelectedMilestone(selectedMilestoneToSet));
      navigate(`/project/${project.id}/milestone/${selectedMilestoneToSet.id}`);
    }
  };

  const handleMilestoneChange = (event: SelectChangeEvent<string>) => {
    if (selectedProject) {
      const milestone = selectedProject.milestones.find(
        (m) => m.name === event.target.value
      );
      if (milestone && milestone.id !== selectedMilestone.id) {
        dispatch(setSelectedMilestone(milestone));
        navigate(`/project/${selectedProject.id}/milestone/${milestone.id}`);
      } else if (!milestone) {
        dispatch(setSelectedMilestone(defaultMilestone));
        navigate(`/project/${selectedProject.id}/milestone/${defaultMilestone.id}`);
      }
    }
  };

  const handleFetchTimeEntries = useCallback(
    (milestoneId: number) => {
      dispatch(fetchClockifyTimeEntriesAsync(milestoneId));
    },
    [dispatch]
  );

  const handleSetClockifyPage = useCallback(
    (page: number) => {
      dispatch(setClockifyPage(page));
    },
    [dispatch]
  );

  const handleSetClockifyPageSize = useCallback(
    (pageSize: number) => {
      dispatch(setClockifyPageSize(pageSize));
    },
    [dispatch]
  );

  const handleSetClockifySortConfig = useCallback(
    (config: any) => {
      dispatch(setClockifySortConfig(config));
    },
    [dispatch]
  );

  const handleSortTimeEntries = useCallback(() => {
    dispatch(sortTimeEntries());
  }, [dispatch]);

  const handleFetchTaskReports = useCallback(
    (milestoneId: number) => {
      dispatch(fetchTaskReportsAsync(milestoneId));
    },
    [dispatch]
  );

  const handleSetTaskReportsPage = useCallback(
    (page: number) => {
      dispatch(setTaskReportsPage(page));
    },
    [dispatch]
  );

  const handleSetTaskReportsPageSize = useCallback(
    (pageSize: number) => {
      dispatch(setTaskReportsPageSize(pageSize));
    },
    [dispatch]
  );

  const handleSetTaskReportsSortConfig = useCallback(
    (config: any) => {
      dispatch(setTaskReportsSortConfig(config));
    },
    [dispatch]
  );

  const handleSortTaskReports = useCallback(() => {
    dispatch(sortTaskReports());
  }, [dispatch]);

  const isAlertsLoading =
    milestoneTaskEstimateAlertInitializationStatus === "initializing" ||
    clockifyTimeEntryTimeAlertInitializationStatus === "initializing" ||
    percentageCompleteChangedAlertInitializationStatus === "initializing";

  const hasValidTypeOfWork = useMemo(() => {
    if (timeEntries.length === 0) return false;
    return timeEntries.some(
      (entry) => entry.typeOfWork && entry.typeOfWork !== "N/A"
    );
  }, [timeEntries]);

  const unassignedMilestone = useMemo(
    () => (selectedProject?.milestones || []).find((m) => m.name === "Unassigned"),
    [selectedProject?.milestones]
  );

  const hasRealMilestones = useMemo(
    () => (selectedProject?.milestones || []).some((m) => m.name !== "Unassigned"),
    [selectedProject?.milestones]
  );

  const groupedMilestoneItems = useMemo(() => {
    const milestones = (selectedProject?.milestones || [])
      .filter((m) => m.name !== "Unassigned");
    const sortByDate = (a: Milestone, b: Milestone) =>
      new Date(b.gitLabCreateDate).getTime() - new Date(a.gitLabCreateDate).getTime();
    const active = milestones
      .filter((m) => (m.state || "").toLowerCase() !== "closed")
      .sort(sortByDate);
    const closed = milestones
      .filter((m) => (m.state || "").toLowerCase() === "closed")
      .sort(sortByDate);

    if (closed.length === 0) {
      return active.map((m) => (
        <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>
      ));
    }

    const items: React.ReactNode[] = [];
    if (active.length > 0) {
      items.push(<ListSubheader key="active-header">Active</ListSubheader>);
      active.forEach((m) =>
        items.push(<MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>)
      );
    }
    items.push(<ListSubheader key="closed-header">Closed</ListSubheader>);
    closed.forEach((m) =>
      items.push(<MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>)
    );
    return items;
  }, [selectedProject?.milestones]);

  return (
    <>
      <Container
        className="App"
        maxWidth="xl"
        sx={{
          display: "flex",
          flexDirection: "column",
          pt: 4,
          pb: 6,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: "-0.5px",
              }}
            >
              Project Progress
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              {hasRealMilestones && (
                <FormControl sx={{ minWidth: 180 }} size="small">
                  <InputLabel>Milestone</InputLabel>
                  <Select
                    value={selectedMilestone?.name === "Unassigned" ? "" : selectedMilestone?.name || ""}
                    label="Milestone"
                    onChange={handleMilestoneChange}
                    disabled={!selectedProject || !isMilestoneFetchCompleted}
                    MenuProps={{ PaperProps: { style: { maxHeight: "70vh" } } }}
                  >
                    {groupedMilestoneItems}
                  </Select>
                </FormControl>
              )}
              {hasRealMilestones && unassignedMilestone && (
                <Chip
                  label="Unassigned"
                  variant={selectedMilestone?.name === "Unassigned" ? "filled" : "outlined"}
                  color={selectedMilestone?.name === "Unassigned" ? "primary" : "default"}
                  onClick={() => {
                    if (selectedMilestone?.name === "Unassigned") {
                      const nonUnassigned = (selectedProject?.milestones || []).filter(
                        (m) => m.name !== "Unassigned"
                      );
                      const fallback = nonUnassigned[0] || defaultMilestone;
                      dispatch(setSelectedMilestone(fallback));
                      navigate(`/project/${selectedProject.id}/milestone/${fallback.id}`);
                    } else {
                      dispatch(setSelectedMilestone(unassignedMilestone));
                      navigate(`/project/${selectedProject.id}/milestone/${unassignedMilestone.id}`);
                    }
                  }}
                  sx={{ height: 32, cursor: "pointer" }}
                />
              )}
              <RunImportButton />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Track milestone progress, burnup metrics, and team performance
          </Typography>
          <ProjectProgressReport
            projects={projects}
            selectedProject={selectedProject}
            selectedMilestone={selectedMilestone}
            handleProjectChange={handleProjectChange}
            handleMilestoneChange={handleMilestoneChange}
          />
        </Box>
        <Box sx={{ mt: 4 }}>
          {selectedMilestone && selectedMilestone.id > 0 && (
            <Box sx={{ mb: 2 }}>
              <Divider sx={{ mb: 3 }} />
              <SnapshotComparison selectedMilestone={selectedMilestone} />
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          {selectedMilestone && selectedMilestone.id > 0 && (
            <Box sx={{ mb: 2 }}>
              <Divider sx={{ mb: 3 }} />
              <MilestoneNotes milestoneId={selectedMilestone.id} />
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          {userRole === "Admin" && selectedMilestone && selectedMilestone.id > 0 && (
            <Box sx={{ mb: isDeveloperDashboardExpanded ? 4 : 2 }}>
              <Divider sx={{ mb: 3 }} />
              <DeveloperDashboard
                tasks={taskReports}
                timeEntries={timeEntries}
                loading={taskReportsLoading || loading || !isMilestoneFetchCompleted}
                isExpanded={isDeveloperDashboardExpanded}
                showExpandButton={true}
                onExpandClick={() =>
                  setIsDeveloperDashboardExpanded(!isDeveloperDashboardExpanded)
                }
              />
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          {selectedMilestone && selectedMilestone.id > 0 && (
            <Box sx={{ mb: isTaskReportsExpanded ? 4 : 2 }}>
              <Divider sx={{ mb: 3 }} />
              <TaskReportTable
                milestoneId={selectedMilestone.id}
                taskReports={taskReports}
                loading={taskReportsLoading || !isMilestoneFetchCompleted}
                pagination={taskReportsPagination}
                sortConfig={taskReportsSortConfig}
                sortApplied={taskReportsSortApplied}
                onFetchTaskReports={handleFetchTaskReports}
                onSetPage={handleSetTaskReportsPage}
                onSetPageSize={handleSetTaskReportsPageSize}
                onSetSortConfig={handleSetTaskReportsSortConfig}
                onSortTaskReports={handleSortTaskReports}
                isExpanded={isTaskReportsExpanded}
                showExpandButton={true}
                onExpandClick={() =>
                  setIsTaskReportsExpanded(!isTaskReportsExpanded)
                }
              />
            </Box>
          )}
        </Box>
        <Box>
          {selectedMilestone && selectedMilestone.id > 0 && (
            <Box sx={{ mb: isClockifyTimeEntriesExpanded ? 4 : 2 }}>
              <Divider sx={{ mb: 3 }} />
              <ClockifyTimeEntryTable
                milestoneId={selectedMilestone.id}
                selectedMilestone={selectedMilestone}
                timeEntries={timeEntries}
                loading={loading || !isMilestoneFetchCompleted}
                pagination={pagination}
                sortConfig={sortConfig}
                sortApplied={sortApplied}
                onFetchTimeEntries={handleFetchTimeEntries}
                onSetPage={handleSetClockifyPage}
                onSetPageSize={handleSetClockifyPageSize}
                onSetSortConfig={handleSetClockifySortConfig}
                onSortTimeEntries={handleSortTimeEntries}
                isExpanded={isClockifyTimeEntriesExpanded}
                showExpandButton={true}
                onExpandClick={() =>
                  setIsClockifyTimeEntriesExpanded(
                    !isClockifyTimeEntriesExpanded
                  )
                }
              />
            </Box>
          )}
        </Box>
        <Box>
          {selectedMilestone &&
            selectedMilestone.id > 0 &&
            (hasValidTypeOfWork || loading || !isMilestoneFetchCompleted) && (
              <Box sx={{ mb: isTypeOfWorkSummaryExpanded ? 4 : 2 }}>
                <Divider sx={{ mb: 3 }} />
                <TypeOfWorkSummaryTable
                  timeEntries={timeEntries}
                  selectedMilestone={selectedMilestone}
                  loading={loading || !isMilestoneFetchCompleted}
                  isExpanded={isTypeOfWorkSummaryExpanded}
                  showExpandButton={true}
                  onExpandClick={() =>
                    setIsTypeOfWorkSummaryExpanded(!isTypeOfWorkSummaryExpanded)
                  }
                />
              </Box>
            )}
        </Box>
        <Box>
          {selectedMilestone && selectedMilestone.id > 0 && (
            <Box sx={{ mb: isAlertsExpanded ? 4 : 2 }}>
              <Divider sx={{ mb: 3 }} />
              <AlertsTable
                milestoneTaskEstimateAlerts={milestoneTaskEstimateAlerts}
                clockifyTimeChangedAlerts={clockifyTimeChangedAlerts}
                percentageCompleteChangedAlerts={
                  percentageCompleteChangedAlerts
                }
                timeEntries={timeEntries}
                milestoneTasks={taskReports}
                loading={isAlertsLoading || !isMilestoneFetchCompleted}
                initializationStatus={initializationStatus}
                isExpanded={isAlertsExpanded}
                showExpandButton={true}
                onExpandClick={() => setIsAlertsExpanded(!isAlertsExpanded)}
              />
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default ProjectProgressPage;
